import { Stack } from '@mui/joy'
import { useStore, useStoreMap } from 'effector-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { repairOrderModel } from '~/entities/RepairOrder'
import { usePermissions } from '~/entities/viewer'
import {
  editRepairOrderModel,
  EditRepairOrder,
  Status,
  PaymentStatus,
} from '~/features/RepairOrders/EditRepairOrder'
import { Breadcrumbs, BreadcrumbsList } from '~/shared/ui/Breadcrumbs'
import { Container, PageTitle, PageContainer } from '~/shared/ui/Layout'
import { NotFound } from '~/shared/ui/NotFound'
import {
  QuickNavigation,
  QuickNavigationOptions,
} from '~/shared/ui/QuickNavigation'
import { AuditsModal } from '~/widgets/AuditsModal'

const quickNavigationOptions: QuickNavigationOptions = [
  { labelI18n: 'form.car', anchor: 'car' },
  { labelI18n: 'form.description repair', anchor: 'descriptionRepair' },
  { labelI18n: 'form.sto', anchor: 'sto' },
  { labelI18n: 'form.cost', anchor: 'cost' },
  { labelI18n: 'form.files', anchor: 'files' },
]

export const RoadAccidentEditRepairOrderPage = () => {
  const { t } = useTranslation()
  const { repairOrderId, roadAccidentId } = useParams<{
    repairOrderId: string
    roadAccidentId: string
  }>()

  const { canAuditsView } = usePermissions()

  const isLoading = useStore(repairOrderModel.requestFx.pending)
  const name = useStoreMap(editRepairOrderModel.$repairOrder, (repairOrder) =>
    repairOrder?.getNumber(),
  )

  const title = `№ ${name || '...'}`

  const breadcrumbs: BreadcrumbsList = useMemo(
    () => [
      { titleI18n: 'road accidents', link: '/road-accidents' },
      {
        title: `${t('road accident')} № ${roadAccidentId}`,
        link: `/road-accidents/${roadAccidentId}`,
      },
      { title },
    ],
    [roadAccidentId, t, title],
  )

  const error = repairOrderModel.useRepairOrderError(repairOrderId as string)

  if (!repairOrderId || error) {
    return <NotFound message={t('alert.repair order load error')} />
  }

  return (
    <PageContainer title={t('repair order card')}>
      <Container isHeader>
        <Breadcrumbs list={breadcrumbs} lastLoading={isLoading} />
        <Stack direction='row' alignItems='end' spacing={2}>
          <Status />
          <PaymentStatus />
        </Stack>
        <Stack direction='row' alignItems='end' justifyContent='space-between'>
          <PageTitle loading={isLoading}>{title}</PageTitle>
          {canAuditsView && <AuditsModal auditableId={repairOrderId} />}
        </Stack>
      </Container>

      <EditRepairOrder repairOrderId={repairOrderId} />

      <QuickNavigation options={quickNavigationOptions} />
    </PageContainer>
  )
}
